/**
 * QC derivation.
 *
 * HONESTY RULE FOR THIS FILE: OpenADS does not emit a QC report. Everything
 * here is either (a) a presence check over outputs the pipeline really writes,
 * (b) a number the pipeline really computes (`metrics.json`, the radiological
 * report), or (c) explicitly marked `not_computed`. Nothing is invented, and no
 * pass/fail threshold is applied to a continuous metric — Dice has no
 * clinically agreed cut-off, so it is reported as a value, never as a verdict.
 */

import type { CaseManifest, ResultsSummary, SegmentationMetrics } from "@/ipc/contract";

export type CheckStatus = "pass" | "warning" | "unavailable" | "not_computed";

export interface QcCheck {
  id: string;
  label: string;
  status: CheckStatus;
  detail: string;
}

export interface QcDetail {
  id: string;
  label: string;
  /** Formatted value, or null when the pipeline does not produce it. */
  value: string | null;
  /** Set when `value` is null and the reason should be shown to the user. */
  note?: string;
}

export interface QcReport {
  overall: "pass" | "warning" | "unknown";
  summary: string;
  checks: QcCheck[];
  details: QcDetail[];
}

/** Prefers post-processed MNI metrics, which are what the report quotes. */
function bestMetrics(results: ResultsSummary | null): SegmentationMetrics | null {
  const file = results?.dwiMetrics ?? results?.pwiMetrics ?? null;
  if (!file) return null;
  return file.mniPostproc ?? file.mni ?? file.origPostproc ?? file.orig ?? null;
}

function fmt(value: number | null | undefined, digits = 3): string | null {
  return value == null || Number.isNaN(value) ? null : value.toFixed(digits);
}

export function deriveQcReport(
  manifest: CaseManifest | null,
  results: ResultsSummary | null,
): QcReport {
  const checks: QcCheck[] = [];

  const maskCount = manifest?.masks.length ?? 0;
  checks.push({
    id: "segmentation",
    label: "Segmentation mask written",
    status: maskCount > 0 ? "pass" : "warning",
    detail: maskCount > 0 ? `${maskCount} mask(s) in the case` : "No mask found in the case outputs",
  });

  const transformCount = manifest?.transforms.length ?? 0;
  checks.push({
    id: "registration",
    label: "Registration transform written",
    status: transformCount > 0 ? "pass" : "warning",
    detail:
      transformCount > 0
        ? `${transformCount} transform(s) in the case`
        : "No affine or warp file found; the run may not have reached registration",
  });

  const reportCount = manifest?.reports.length ?? results?.reports.length ?? 0;
  checks.push({
    id: "report",
    label: "Radiological report generated",
    status: reportCount > 0 ? "pass" : "warning",
    detail: reportCount > 0 ? `${reportCount} report file(s)` : "No report produced",
  });

  const metrics = bestMetrics(results);
  checks.push({
    id: "groundTruth",
    label: "Ground-truth metrics available",
    // Absence is normal in clinical use — it is not a failure, so it is
    // "unavailable" rather than a warning.
    status: metrics ? "pass" : "unavailable",
    detail: metrics
      ? "metrics.json found; Dice and precision below"
      : "No ground-truth lesion mask was supplied, so overlap metrics were not computed",
  });

  const warnings = results?.warnings ?? [];
  checks.push({
    id: "warnings",
    label: "Pipeline warnings",
    status: warnings.length === 0 ? "pass" : "warning",
    detail: warnings.length === 0 ? "None reported" : warnings.join("; "),
  });

  // PLACEHOLDER — the mockup lists this check; OpenADS does not compute it.
  checks.push({
    id: "edgeConcordance",
    label: "Edge concordance",
    status: "not_computed",
    detail: "Not produced by the OpenADS pipeline",
  });

  const details: QcDetail[] = [
    {
      id: "coreVolume",
      label: "Lesion volume (mL)",
      value: fmt(results?.coreVolumeMl ?? null, 2),
      note: "Parsed from the radiological report",
    },
    {
      id: "lesionVoxels",
      label: "Lesion voxels",
      value: metrics?.predVolumeVox != null ? String(metrics.predVolumeVox) : null,
      note: "From metrics.json",
    },
    { id: "dice", label: "Dice (vs ground truth)", value: fmt(metrics?.dice), note: "Needs a ground-truth mask" },
    { id: "precision", label: "Precision", value: fmt(metrics?.precision), note: "Needs a ground-truth mask" },
    {
      id: "sensitivity",
      label: "Sensitivity",
      value: fmt(metrics?.sensitivity),
      note: "Needs a ground-truth mask",
    },
    {
      id: "fdr",
      label: "False discovery rate",
      // 1 − precision is exactly the false discovery rate; the mockup's
      // "False Positive Rate" would need a true-negative count the pipeline
      // never reports, so the correct name is used here.
      value: metrics?.precision != null ? (1 - metrics.precision).toFixed(3) : null,
      note: "Derived from precision; needs a ground-truth mask",
    },
    {
      id: "meanDistance",
      label: "Mean surface distance (mm)",
      value: null,
      note: "PLACEHOLDER — not computed by the pipeline",
    },
    {
      id: "hausdorff95",
      label: "95th percentile distance (mm)",
      value: null,
      note: "PLACEHOLDER — not computed by the pipeline",
    },
  ];

  const hasWarning = checks.some((check) => check.status === "warning");
  const anyEvidence = manifest !== null || results !== null;
  return {
    overall: !anyEvidence ? "unknown" : hasWarning ? "warning" : "pass",
    summary: !anyEvidence
      ? "No case open."
      : hasWarning
        ? "Expected outputs are missing or the pipeline reported warnings."
        : "All expected pipeline outputs are present.",
    checks,
    details,
  };
}
